import { useMemo, type CSSProperties } from 'react';
import type { Theme } from '@goodtime/engine';
import {
  WHEEL_PODIUM_MULTIPLIER_PERCENT,
  wheelPodiumMultiplierLabel,
  type BotArchetype,
  type WheelObservableState,
  type WheelSeatView,
} from '@goodtime/shared';
import { SeatAvatar, useSeatName } from './SeatAvatar';
import { Icon } from '../Icon';
import { wheelBiomeBackdropUrl } from './wheelBiomeBackdrop';
import type { ReceivedReaction } from '../../hooks/useWheelRoomStream';
import { ReactionFloat } from './ReactionFloat';
import podiumArtUrl from '../../assets/wheel/podium.png';

export interface WheelPayoutViewProps {
  state: WheelObservableState;
  selfSeatId?: number | null;
  /** Active room theme — drives the biome-panorama backdrop. */
  theme?: Theme | null;
  /** R1.4 (2026-05-28) — live FIFO buffer of recent reactions from
   *  {@link useWheelRoomStream}. PAYOUT is the natural celebration
   *  moment; bots auto-emit at 5× probability in this phase. The
   *  match-wrap renders {@link ReactionFloat} above each podium-row
   *  avatar so the reactions land where the crowd's eyes already are. */
  reactions?: ReadonlyArray<ReceivedReaction>;
  /** U11 (2026-05-30) — fires when the player taps a bot's avatar
   *  in the podium list. The host (WheelLobbyView) opens a
   *  {@link ProfileCard} with hook-driven favourite state. PLAYER
   *  seats and EMPTY seats are no-op for the tap (PLAYER carries no
   *  archetype). Null/omitted disables the tap entirely — used in
   *  the design-review scaffold where the cast picker doesn't make
   *  sense. */
  onArchetypeTap?: ((archetype: BotArchetype) => void) | null;
}

const EMPTY_REACTIONS: ReadonlyArray<ReceivedReaction> = [];

/**
 * Where each winner stands on the commissioned podium graphic
 * ({@code assets/wheel/podium.png}). {@code xPct} is the tier's
 * horizontal centre and {@code topPct} is where the avatar's feet sit
 * (a touch onto the platform), both as a % of the square art. Measured
 * from the rendered art — tier tops sit at ~51% (centre/1st), ~58%
 * (left/2nd), ~65% (right/3rd); topPct adds a few % so the avatar rests
 * ON the platform rather than at its back edge.
 */
const PODIUM_TIERS: ReadonlyArray<{
  rank: number;
  xPct: number;
  topPct: number;
  avatarSize: number;
  accent: string;
}> = [
  { rank: 1, xPct: 50, topPct: 55, avatarSize: 58, accent: '#FFC940' },
  { rank: 2, xPct: 20, topPct: 59, avatarSize: 48, accent: '#C8C8E0' },
  { rank: 3, xPct: 80, topPct: 64, avatarSize: 48, accent: '#D49A6A' },
];

/**
 * U7 — Match end. Shows the final per-seat winnings + standings then
 * the server auto-progresses to IDLE → CHARGING for the next match.
 * Brief screen (5s by default) — long enough to read the result, short
 * enough to keep the loop tight.
 */
export function WheelPayoutView({
  state,
  selfSeatId = null,
  theme = null,
  reactions = EMPTY_REACTIONS,
  onArchetypeTap = null,
}: WheelPayoutViewProps) {
  const biomeBackdropUrl = wheelBiomeBackdropUrl(theme);

  const standings = useMemo(() => {
    const totals = new Map<number, number>();
    // Spatial-dial flow — per-seat outcome on dialResolveResult.
    if (state.dialResolveResult) {
      for (const [k, outcome] of Object.entries(
        state.dialResolveResult.perSeatOutcomes,
      )) {
        totals.set(Number(k), outcome.payout);
      }
    } else {
      // Legacy three-sub-spin flow — sum payouts across results.
      for (const r of state.subSpinResults) {
        for (const [k, v] of Object.entries(r.payouts)) {
          const id = Number(k);
          totals.set(id, (totals.get(id) ?? 0) + (v as number));
        }
      }
    }
    // Producer brief 2026-05-15: sort by final TOKENS (settled +
    // leaderboard bonus, which the server has already applied to
    // seat.tokens by the time we render this view) — that's the
    // ranking the bonus schedule rewards, and the order the player
    // is competing for. Ties break by lower seatId to match the
    // server's deterministic resolution. The per-row "won X coins
    // this round" line still shows the payout amount.
    return state.seats
      .map((s) => ({ seat: s, payout: totals.get(s.seatId) ?? 0 }))
      .sort((a, b) => {
        const tokenDiff = b.seat.tokens - a.seat.tokens;
        if (tokenDiff !== 0) return tokenDiff;
        return a.seat.seatId - b.seat.seatId;
      });
  }, [state.seats, state.subSpinResults, state.dialResolveResult]);

  const selfRow = standings.find((r) => r.seat.seatId === selfSeatId) ?? null;
  /** Producer brief 2026-05-16 — surface the self-row podium
   *  multiplier in the headline so the player sees the boost on
   *  their winnings without having to scan rows. */
  const selfRowRank = selfRow
    ? standings.findIndex((r) => r.seat.seatId === selfRow.seat.seatId) + 1
    : 0;
  const selfRowPodium = selfRowRank >= 1 && selfRowRank <= 3;
  const selfRowMultiplierPercent =
    selfRowPodium ? WHEEL_PODIUM_MULTIPLIER_PERCENT[selfRowRank - 1] ?? 0 : 0;
  const selfRowMultiplierLabel = selfRowPodium
    ? wheelPodiumMultiplierLabel(selfRowRank)
    : '';
  const selfRowMultiplierGain =
    selfRow && selfRowMultiplierPercent > 0
      ? Math.floor((selfRow.payout * selfRowMultiplierPercent) / 100)
      : 0;

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflowY: 'auto',
        background:
          'radial-gradient(ellipse at top, #2A4A1A 0%, #142810 60%, #060d22 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        // Respect the notch / Dynamic Island so the heading clears it
        // (the old fixed 24px tucked the title under the island once the
        // eyebrow row was removed — producer 2026-06-10).
        padding: 'calc(env(safe-area-inset-top, 0px) + 24px) 12px 80px',
      }}
    >
      {/* Biome-panorama backdrop — matches the Reel Race surface so
          the match wrap reads as the same room cooling off, not a
          separate "results" screen. */}
      {biomeBackdropUrl ? (
        <img
          src={biomeBackdropUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.45,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 0%, transparent 35%, rgba(0,0,0,0.6) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800 }}>
          Winnings
        </div>
        {selfRow ? (
          <div
            style={{
              marginTop: 10,
              fontSize: 19,
              fontWeight: 800,
              color: selfRow.payout > 0 ? '#9DE54E' : 'rgba(255,255,255,0.7)',
            }}
          >
            You won {selfRow.payout} {selfRow.payout === 1 ? 'coin' : 'coins'}
            {selfRowPodium && selfRowMultiplierGain > 0 ? (
              <span style={{ color: '#FFC940', marginLeft: 6 }}>
                · {selfRowMultiplierLabel} podium (+{selfRowMultiplierGain})
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Podium — top 3 stand on the commissioned 2 · 1 · 3 podium
          graphic (producer 2026-06-08), 1st centre/tallest. Avatar +
          name + coins ride above each winner; the podium-bonus pills sit
          in a row BELOW the podium (the top was too text-heavy). Ranks
          4+ stay in the flat list below. */}
      {standings.length > 0 ? (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 380,
            margin: '0 auto 0',
            aspectRatio: '1 / 1',
          }}
        >
          <img
            src={podiumArtUrl}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.55))',
            }}
          />
          {PODIUM_TIERS.map((tier) => {
            const row = standings[tier.rank - 1];
            if (!row) return null;
            return (
              <PodiumWinner
                key={row.seat.seatId}
                tier={tier}
                seat={row.seat}
                payout={row.payout}
                isSelf={selfSeatId !== null && row.seat.seatId === selfSeatId}
                reactions={reactions}
                onArchetypeTap={onArchetypeTap}
              />
            );
          })}
        </div>
      ) : null}

      {/* Podium-bonus pills — one per winner, in a row below the podium
          aligned under each tier (moved down from above the avatars,
          producer 2026-06-08). */}
      {standings.length > 0 ? (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 380,
            height: 22,
            margin: '-12px auto 18px',
          }}
        >
          {PODIUM_TIERS.map((tier) => {
            const row = standings[tier.rank - 1];
            if (!row) return null;
            const pct = WHEEL_PODIUM_MULTIPLIER_PERCENT[tier.rank - 1] ?? 0;
            const gain =
              row.payout > 0 ? Math.floor((row.payout * pct) / 100) : 0;
            if (gain <= 0) return null;
            // Spread the pills a little wider than the avatars (×1.17 the
            // offset from centre, so ~15 / 50 / 85%) so they breathe
            // without drifting too far out (producer 2026-06-08).
            const pillX = 50 + (tier.xPct - 50) * 1.17;
            return (
              <span
                key={tier.rank}
                style={{
                  position: 'absolute',
                  left: `${pillX}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  background: `${tier.accent}26`,
                  border: `1px solid ${tier.accent}aa`,
                  color: tier.accent,
                  borderRadius: 999,
                  padding: '2px 9px',
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: 0.3,
                  whiteSpace: 'nowrap',
                }}
              >
                {wheelPodiumMultiplierLabel(tier.rank)} · +{gain}
              </span>
            );
          })}
        </div>
      ) : null}

      {standings.length > 3 ? (
        <ol
          style={{
            position: 'relative',
            zIndex: 2,
            margin: '0 auto',
            padding: 0,
            listStyle: 'none',
            maxWidth: 460,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {standings.slice(3).map((row, idx) => (
            <PayoutRow
              key={row.seat.seatId}
              seat={row.seat}
              payout={row.payout}
              rank={idx + 4}
              podiumMultiplierPercent={0}
              isSelf={selfSeatId !== null && row.seat.seatId === selfSeatId}
              reactions={reactions}
              onArchetypeTap={onArchetypeTap}
            />
          ))}
        </ol>
      ) : null}

      {/* Reaction tray moved to the room level (WheelLobbyView) so it
          sits in one consistent bottom-right spot across every phase.
          Bots still auto-emit during PAYOUT; the per-seat floats here
          render from the `reactions` prop. */}
    </div>
  );
}

/**
 * One winner standing on the commissioned podium graphic. Positioned
 * absolutely over the art at its tier (see {@link PODIUM_TIERS}): the
 * avatar's feet sit on the platform and the info (name, coins, podium
 * chip) stacks upward into the empty space above the podium. The group
 * itself is pointer-transparent so only the avatar is tappable.
 */
function PodiumWinner({
  tier,
  seat,
  payout,
  isSelf,
  reactions,
  onArchetypeTap,
}: {
  tier: (typeof PODIUM_TIERS)[number];
  seat: WheelSeatView;
  payout: number;
  isSelf: boolean;
  reactions: ReadonlyArray<ReceivedReaction>;
  onArchetypeTap: ((archetype: BotArchetype) => void) | null;
}) {
  const name = useSeatName(seat);
  const { xPct, topPct, avatarSize, accent } = tier;

  const avatarInner = (
    <>
      <SeatAvatar seat={seat} size={avatarSize} />
      <ReactionFloat reactions={reactions} seatId={seat.seatId} />
    </>
  );
  const ringStyle: CSSProperties = {
    position: 'relative',
    display: 'flex',
    borderRadius: '50%',
    boxShadow: `0 0 0 3px ${accent}, 0 0 14px ${accent}66`,
    pointerEvents: 'auto',
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: `${xPct}%`,
        top: `${topPct}%`,
        transform: 'translate(-50%, -100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        width: 116,
        pointerEvents: 'none',
        textShadow: '0 1px 3px rgba(0,0,0,0.75)',
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          color: payout > 0 ? '#FFE9A6' : 'rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Icon name="coin" size={14} alt="" />
        {payout}
      </div>

      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: isSelf ? '#FFC940' : '#FFFFFF',
          textAlign: 'center',
          lineHeight: 1.15,
          maxWidth: '100%',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          marginBottom: 2,
        }}
      >
        {name}
        {isSelf ? ' · you' : ''}
      </div>

      {onArchetypeTap !== null && seat.archetype !== null ? (
        <button
          type="button"
          onClick={() => onArchetypeTap(seat.archetype as BotArchetype)}
          aria-label={`Open ${name} profile`}
          style={{ ...ringStyle, padding: 0, margin: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
        >
          {avatarInner}
        </button>
      ) : (
        <div style={ringStyle}>{avatarInner}</div>
      )}
    </div>
  );
}

function PayoutRow({
  seat,
  payout,
  rank,
  podiumMultiplierPercent,
  isSelf,
  reactions,
  onArchetypeTap,
}: {
  seat: WheelSeatView;
  payout: number;
  rank: number;
  /** Match-end podium multiplier percent (e.g. 100 for 1st = ×2).
   *  The bonus coins ({@code payout × percent / 100}) are already
   *  baked into {@code seat.tokens} by the server. Surface the
   *  multiplier as a chip on the row so the player reads WHY their
   *  final total is higher than the raw dial winnings. 0 for
   *  non-podium ranks. */
  podiumMultiplierPercent: number;
  isSelf: boolean;
  /** R1.4 — reactions feed; per-row filter happens inside
   *  {@link ReactionFloat}. */
  reactions: ReadonlyArray<ReceivedReaction>;
  /** U11 (2026-05-30) — tap-target callback for the seat's avatar.
   *  Fires only when the seat has an archetype (BOT seats only).
   *  Null/omitted disables the tap affordance entirely. */
  onArchetypeTap: ((archetype: BotArchetype) => void) | null;
}) {
  const name = useSeatName(seat);
  const isPodium = rank <= 3 && podiumMultiplierPercent > 0;
  const multiplierLabel = isPodium ? wheelPodiumMultiplierLabel(rank) : '';
  const multiplierGain =
    isPodium && payout > 0
      ? Math.floor((payout * podiumMultiplierPercent) / 100)
      : 0;
  const podiumAccent =
    rank === 1 ? '#FFC940' : rank === 2 ? '#C8C8E0' : rank === 3 ? '#D49A6A' : null;
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        background: isSelf ? 'rgba(255, 201, 64, 0.12)' : 'rgba(255,255,255,0.04)',
        border: isSelf
          ? '1px solid rgba(255, 201, 64, 0.5)'
          : isPodium
            ? `1px solid ${podiumAccent}55`
            : '1px solid rgba(255,255,255,0.05)',
        borderRadius: 12,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          width: 22,
          textAlign: 'center',
          color: podiumAccent ?? 'rgba(255,255,255,0.55)',
        }}
      >
        {rank}
      </div>
      {/* R1.4 — host the seat's ReactionFloat over the avatar.
          The float component anchors itself with absolute positioning
          (left:50%, bottom:50%), so this wrapper needs to be the
          relatively-positioned container.
          U11 — when {@link onArchetypeTap} is wired AND the seat
          carries an archetype, the wrapper becomes a tap target that
          opens the cast member's ProfileCard. PLAYER seats render
          the same wrapper as a non-interactive div since they have
          no archetype to look up. */}
      {onArchetypeTap !== null && seat.archetype !== null ? (
        <button
          type="button"
          onClick={() => onArchetypeTap(seat.archetype as BotArchetype)}
          aria-label={`Open ${name} profile`}
          style={{
            position: 'relative',
            padding: 0,
            margin: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            // Subtle hover ring without growing the avatar's bounding
            // box (which would shift the surrounding podium chrome).
            borderRadius: '50%',
          }}
        >
          <SeatAvatar seat={seat} size={36} />
          <ReactionFloat reactions={reactions} seatId={seat.seatId} />
        </button>
      ) : (
        <div style={{ position: 'relative' }}>
          <SeatAvatar seat={seat} size={36} />
          <ReactionFloat reactions={reactions} seatId={seat.seatId} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {name}
          {isSelf ? <span style={{ color: '#FFC940', marginLeft: 6 }}>· you</span> : null}
        </div>
        {/* Subtitle removed the "N reel-race tokens" line (producer
            2026-06-08: "remove the reel-race token text, I don't even know
            what that is"). Only the podium-bonus chip stays — and only on
            podium rows, so non-podium rows are just name + coins. */}
        {isPodium ? (
          <div
            style={{
              fontSize: 11,
              opacity: 0.65,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                background: 'rgba(255, 201, 64, 0.22)',
                border: `1px solid ${podiumAccent}99`,
                color: podiumAccent ?? '#FFC940',
                borderRadius: 999,
                padding: '1px 6px',
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: 0.5,
                whiteSpace: 'nowrap',
              }}
              title={
                multiplierGain > 0
                  ? `${multiplierLabel} winnings · +${multiplierGain}`
                  : `${multiplierLabel} podium`
              }
            >
              {multiplierLabel} podium
              {multiplierGain > 0 ? ` · +${multiplierGain}` : ''}
            </span>
          </div>
        ) : null}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          color: payout > 0 ? '#FFC940' : 'rgba(255,255,255,0.4)',
          fontVariantNumeric: 'tabular-nums',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Icon name="coin" size={16} alt="" />
        {payout}
      </div>
    </li>
  );
}
